import deepmerge from 'deepmerge';
import type { Config, DeepPartial } from '../default/types';
import defaultConfig, { getFullLocaleFromConfig, environment, customArrayMerge } from '../default';
import { productConfig } from './product/product';
import { installmentCalculatorConfig } from './product/installmentCalculator';
import { dynamicHelp } from './cms/dynamicHelp';
import { orderConfig } from './order/order';
import { paymentMethodsConfig } from './payment';

const tenantId = 'quellede';

const config = deepmerge<Config, DeepPartial<Config>>(
  defaultConfig,
  {
    host: { name: tenantId, domain: 'quelle.de', appDeeplinkProtocol: 'qdeapp' },
    clientId: 'QuelleDe',
    i18n: {
      defaultLocale: 'de-DE',
      locales: ['de-DE'],
      languages: ['de'],
    },
    tenantId,
    company: {
      name: 'Quelle',
      seoTitleName: 'quelle.de',
    },
    navigation: {
      firstLevelFontSize: '1rem',
    },
    headerSlots: {
      serviceApi: `https://storage.googleapis.com/drupal-pages/${environment}/menus/${tenantId}/<locale>/bucket-service-flyout.json`,
    },
    assets: {
      cmsImage: {
        imageOptimizationEnabled: true,
        uriTransformEndpoint: `https://storage.googleapis.com/drupal-images-${tenantId}/$1/`,
      },
      assetServerHost: 'https://bilder.quelle.de',
    },
    staticContent: {
      apiEndpoints: {
        webappJson: `http://cloud-service-cms.${tenantId}.svc.cluster.local:3100`,
        bucket: `https://storage.googleapis.com/drupal-pages/${environment}<subfolder>/${tenantId}/<locale>/<identifier>.json`,
      },
    },
    product: productConfig,
    installmentCalculator: installmentCalculatorConfig,
    search: {
      adServer: {
        mode: 'containerOnly',
      },
      hideTelekomIcon: true,
    },
    middleware: {
      cmsRewrites: {
        exact: ['/', '/storefront', '/storefront-app', '/mafo/unsubscribe', '/uwg7/unsubscribe'],
        prefixes: [
          '/service-hilfe',
          '/lp',
          '/content',
          '/magazin',
          '/node',
          '/servicelayer/',
          '/preview-app/',
          '/mailings/',
        ],
      },
    },
    order: {
      ...orderConfig,
      supportedPaths: {
        myAccountAddresses: true,
      },
      parcelShop: {
        apiKey: 'be22763e-f7e9-4',
        countriesIdentifier: 'DE',
      },
      assets: {
        premium: {
          getPremiumImage: ({ device }: { device: string }) =>
            `https://bilder.quelle.de/empiriecom/io/quellede/de/BS3_CheckOut_${device}.jpg`,
          getPremiumIcon: () =>
            'https://bilder.quelle.de/empiriecom/io/quellede/de/premiumicon_1.svg',
        },
        gifs: {
          success: {
            url: 'https://bilder.quelle.de/i/empiriecom/io/de/thank_you_1_transparent.gif',
          },
        },
      },
      hasFlexikontoAdvantageClub: true,
      hasCo2Compensation: false,
    },
    abTesting: {
      clientSideEnrollment: {
        activeTestIds: [
          process.env.NEXT_PUBLIC_ENVIRONMENT === 'staging'
            ? '2026_3_key_features_test'
            : '2026_3_key_features',
        ],
      },
    },
    footer: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/drupal-pages/${environment}<subfolder>/${tenantId}/<locale>/<identifier>.json`,
      },
    },
    forms: {
      contact: { dynamicHelp },
      newsletterUnsubscribe: {
        useDedicatedSubmitButtonTitleFor: ['unsubscribe|99', 'unsubscribeFlexikonto|98'],
        fields: [
          {
            id: 'newsletter',
            formType: 'radio',
            defaultValue: 'unsubscribe|99',
            options: [
              {
                id: 'frq-daily',
                value: 'frequency|0',
                messageKey: 'forms.newsletter.unsubscribe.type.frequency.option.daily',
              },
              {
                id: 'frq-weekly',
                value: 'frequency|1',
                messageKey: 'forms.newsletter.unsubscribe.type.frequency.option.weekly',
              },
              {
                id: 'unsubscribe',
                value: 'unsubscribe|99',
                messageKey: 'forms.newsletter.unsubscribe.type.unsubscribe',
              },
              {
                id: 'unsubscribe-flexikonto',
                value: 'unsubscribeFlexikonto|98',
                messageKey: 'forms.newsletter.unsubscribe.type.unsubscribeFlexikonto',
              },
              {
                id: 'pause',
                value: 'pause|-100',
                messageKey: 'forms.newsletter.unsubscribe.type.pause',
              },
            ],
            additionalForm: {
              id: 'pause',
              formType: 'select',
              showOnlyWhenWhatSelected: 'pause',
              messageKeyPlaceholder: 'forms.newsletter.unsubscribe.type.pause.label',
              options: [
                {
                  id: 'pause-7',
                  value: '7',
                  messageKey: 'forms.newsletter.unsubscribe.type.pause.option.7',
                },
                {
                  id: 'pause-14',
                  value: '14',
                  messageKey: 'forms.newsletter.unsubscribe.type.pause.option.14',
                },
                {
                  id: 'pause-21',
                  value: '21',
                  messageKey: 'forms.newsletter.unsubscribe.type.pause.option.21',
                },
                {
                  id: 'pause-30',
                  value: '30',
                  messageKey: 'forms.newsletter.unsubscribe.type.pause.option.30',
                },
              ],
            },
          },
          { id: 'email', formType: 'email' },
        ],
      },
      newsletterSubscribe: {
        de: {
          unsubscribeLink:
            'https://www.quelle.de/service-hilfe/newsletter/pausieren-oder-abmelden/',
          voucherLink: 'https://www.quelle.de/service-hilfe/gutscheine-rabatte/',
        },
      },
    },
    banner: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/drupal-contentsnippet/${environment}/${tenantId}/<locale>/<identifier>.json`,
      },
    },
    nlSheetTexts: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/drupal-contentsnippet/${environment}/${tenantId}/<locale>/nl-sheet.json`,
      },
    },
    serviceLayer: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/drupal-contentsnippet/${environment}/${tenantId}/<locale>/<identifier>.json`,
      },
    },
    seoContent: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/inspire-seofiles/<locale>_${tenantId}-<category>.json`,
      },
      url: {
        'de-DE': {
          privacyNoticePath: '/service-hilfe/ueber-uns/datenschutz',
          potentialActionTarget: 'https://www.quelle.de/s/',
        },
      },
    },
    survey: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/drupal-surveys/${environment}/${tenantId}/<locale>/<identifier>.json`,
      },
    },
    promotionBanner: {
      apiEndpoints: {
        bucketEmptyPromotions: `https://storage.googleapis.com/drupal-contentsnippet/${environment}/${tenantId}/<language>/empty-promotion.json`,
        bucketPromotion: `https://storage.googleapis.com/promotion-data/${tenantId}/<language>/<code>.json`,
        bucketContentSnippet: `https://storage.googleapis.com/drupal-contentsnippet/${environment}/${tenantId}/<language>/<identifier>.json`,
      },
    },
    voucher: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/promotion-data/${tenantId}/<locale>/<identifier>.json`,
      },
    },
    appBanner: { appUrl: 'https://app.adjust.com/uvh953o_lmcyu9b' },
    tracking: {
      clarityIdProduction: 'x8xb7aulwd',
      categoryIdHomepage: '101698',
      soastaId: '38EQD-2PT6K-8M2C6-RPG2A-MLT97',
      tagmanagerId: 'GTM-PWVG2XP',
      googleSiteVerificationID: ['CKrb18LGJ__0NQAvYMPOHWM1bPfUEtU6vN14Ji8gVQs'],
      msvalidateID: '4067315BE5669CD6C74BDEC6F6AC5D47',
      schemaOrgOrganisation: {
        'de-DE': {
          '@context': 'https://schema.org',
          '@type': 'Organization',
          '@id': 'https://www.quelle.de/',
          name: 'Quelle',
          alternateName: 'Quelle Versandhandel',
          description:
            'Quelle ist ein deutscher Online-Shop, der Elektrogeräte, Technik, Möbel und vieles mehr anbietet. Quelle bietet eine große Auswahl an Produkten zu günstigen Preisen und ist bekannt für seinen zuverlässigen Service. Quelle ist *nicht* nur ein reiner Online-Händler, sondern ein Anbieter für ein breites Spektrum an Produkten für den täglichen Bedarf.',
          url: 'https://www.quelle.de/',
          logo: 'https://www.quelle.de/assets/logos/quellede/logo.svg',
          foundingDate: '1927-10-26',
          founder: {
            '@type': 'Person',
            name: 'Gustav Schickedanz',
            description: 'Gründer von Quelle',
            sameAs: 'https://de.wikipedia.org/wiki/Gustav_Schickedanz',
          },
          location: {
            '@type': 'Place',
            name: 'Burgkunstadt',
            address: {
              '@type': 'PostalAddress',
              streetAddress: 'Bahnhofstraße 10',
              addressLocality: 'Burgkunstadt',
              addressRegion: 'BY',
              postalCode: '96224',
              addressCountry: 'DE',
            },
          },
          contactPoint: [
            {
              '@type': 'ContactPoint',
              contactType: 'customer service',
              telephone: '+49-9572-3868-411',
              email: 'service@quelle.de',
              availableLanguage: ['de'],
              hoursAvailable: ['Mo-Su 07:00-22:00'],
              url: 'https://www.quelle.de/service-hilfe/beratung',
            },
          ],
          sameAs: [
            'https://www.facebook.com/Quelle/',
            'https://de.wikipedia.org/wiki/Quelle_(Versandhandel)',
          ],
          legalName: 'Quelle GmbH',
          areaServed: 'DE',
          memberOf: {
            '@type': 'Organization',
            name: 'Otto Group',
            sameAs: 'https://www.ottogroup.com/',
          },
          foundingLocation: 'Fürth',
          slogan: 'Quelle - Wir liefern Lösungen.',
        },
      },
    },
    externalScript: {
      kuecheCo: {
        key: 'pAYrueynYRcqna7lGLoVT39kF83Wy3PYENp81Yw0QJqeTyzAjXvc1FwGOzCm',
      },
    },
    headerInformation: {
      de: {
        description:
          'Der Sommer 2020 im Quelle Online Shop: Elektrogeräte, Technik, Möbel und vieles mehr günstig online bestellen ✔ bequeme Ratenzahlung ✔ Gratis Versand ab 75 €',
        title: 'Quelle.de | Elektrogeräte, Möbel und Technik online kaufen',
        canonical: 'https://www.quelle.de',
        robots: 'index,follow',
      },
    },
    // kim is renamed as "customerCentricFeedback"
    customerCentricFeedback: {
      tenantId: '1600',
    },
    dynamicYieldTracking: {
      key: 'a7f5b2847df3d945cc4379520ec60d993f82965e5a43bc03045c1850f3adf1b5',
    },
    geoCountryLayer: {
      enabled: true,
      flag: 'de',
      alternatives: [
        {
          flag: 'ch',
          shops: [{ link: 'https://www.ackermann.ch' }],
        },
        {
          flag: 'at',
          shops: [{ link: 'https://www.ottoversand.at' }],
        },
      ],
    },
    bookings: {
      overview: {
        showOpenPayment: 'sum-and-open',
      },
    },
    chatBot: {
      enabled: true,
      key: '322d1bb170ff472eb10b98397e2ddf69a88622fae51f42e8b87c1c7503f2c9b2',
      includePaths: ['/', '/service-hilfe'],
      excludePaths: [
        '/service-hilfe/newsletter',
        '/service-hilfe/shopping-vorteile',
        '/service-hilfe/nachhaltigkeit',
        '/service-hilfe/ueber-uns',
      ],
    },
    speedKit: {
      enabled: true,
      scriptUrl: 'https://ottoaustria.app.baqend.com/v1/speedkit/install.js',
    },
    ...paymentMethodsConfig,
  },
  { arrayMerge: customArrayMerge },
);

export const getFullLocale = (localeOrLanguage: string | undefined) =>
  getFullLocaleFromConfig(localeOrLanguage, config);

// eslint-disable-next-line import-x/no-default-export
export default config;
